import React, { useState, useEffect } from 'react';
import { fetchWeatherData } from '../api';
import { Typography, Skeleton } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SearchIcon from '@mui/icons-material/Search';
import { Input } from 'antd';

const WeatherData = ({ eventId = null }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [weatherStations, setWeatherStations] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchWeatherData(eventId);
        setWeatherData(data);
        
        // Convert data object to array for rendering
        // Both current weather and event weather now use the same format
        if (data) {
          const stationsArray = Object.entries(data).map(([id, stationData]) => ({
            id: parseInt(id),
            ...stationData
          }));
          setWeatherStations(stationsArray);
        }
      } catch (error) {
        console.error('Error fetching weather data:', error);
        setWeatherData(null);
        setWeatherStations([]);
      }
    };
    fetchData();
  }, [eventId]);

  const renderWeatherItem = (stationData) => {
    const cityNameCapitalized = stationData.station_name;
    const countryFlag = stationData.country === 'Canada' ? '🇨🇦' : '🇺🇸';
  
    const getIcon = (summary) => {
      const forecast = summary ? summary.toLowerCase() : '';
      if (forecast.includes('sunny')) return <WbSunnyIcon fontSize="inherit" />;
      if (forecast.includes('cloudy')) return <CloudIcon fontSize="inherit" />;
      if (forecast.includes('snow')) return <AcUnitIcon fontSize="inherit" />;
      if (forecast.includes('clear')) return <NightsStayIcon fontSize="inherit" />;
      return <CloudIcon fontSize="inherit" />; // default icon
    };
  
    const dayTemperature = stationData.country === 'USA' 
      ? `${stationData.day.temperature}°F` 
      : `${stationData.day.temperature}°C`;
    const nightTemperature = stationData.country === 'USA' 
      ? `${stationData.night.temperature}°F` 
      : `${stationData.night.temperature}°C`;
    const accumulation = stationData.accumulation
      ? `${stationData.accumulation} ${stationData.country === 'Canada' ? 'cm' : 'in'}`
      : 'N/A';

    // Generate weather URL based on country and coordinates
    const weatherUrl = stationData.latitude && stationData.longitude
      ? stationData.country === 'Canada'
        ? `https://weather.gc.ca/en/location/index.html?coords=${stationData.latitude},${stationData.longitude}`
        : `https://forecast.weather.gov/MapClick.php?lat=${stationData.latitude}&lon=${stationData.longitude}`
      : null;

    return (
      <div
        key={stationData.id}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '10px',
          backgroundColor: '#fafafa',
          borderRadius: '10px',
          cursor: weatherUrl ? 'pointer' : 'default',
          transition: 'background-color 0.3s ease',
        }}
        onMouseEnter={(e) => weatherUrl && (e.currentTarget.style.backgroundColor = '#eaeaea')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
        onClick={() => weatherUrl && window.open(weatherUrl, '_blank', 'noopener noreferrer')}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            paddingLeft: '5px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '5px',
            }}
          >
            <span style={{ fontSize: '18px', fontWeight: '500' }}>
              {cityNameCapitalized}
            </span>
            <span style={{ fontSize: '12px' }}>{countryFlag}</span>
          </div>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {stationData.province_state}
          </span>
          <span style={{ fontSize: '14px' }}>
            {`Accumulation: ${accumulation}`}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: 'row', width: '120px' }}>
            <Typography style={{ fontSize: '14px', marginRight: '10px' }}>Day:</Typography>
            {getIcon(stationData.day.summary)}
            <Typography style={{ fontSize: '16px', marginLeft: '5px' }}>{dayTemperature}</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: 'row', width: '120px' }}>
            <Typography style={{ fontSize: '14px', marginRight: '10px' }}>Night:</Typography>
            {getIcon(stationData.night.summary)}
            <Typography style={{ fontSize: '16px', marginLeft: '5px' }}>{nightTemperature}</Typography>
          </div>
        </div>
      </div>
    );
  };

  const renderWeatherSection = (stations) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          gap: '5px',
          width: '100%',
        }}
      >
        {stations.map((stationData) => renderWeatherItem(stationData))}
      </div>
    );
  };

  // Filter stations by search query
  const filteredStations = weatherStations.filter((station) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      station.station_name.toLowerCase().includes(query) ||
      station.province_state.toLowerCase().includes(query) ||
      station.country.toLowerCase().includes(query)
    );
  });

  return (
    <div
      style={{
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
        borderRadius: '5px',
        backgroundColor: 'white',
        padding: '15px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Typography variant="h5" align="left">
        Weather Forecasts
      </Typography>
      <Input
        placeholder="Search city"
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: '100%', marginTop: '10px' }}
        prefix={<SearchIcon style={{ color: '#c2c2c2' }} fontSize="12px" />}
      />
      {!weatherData ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '100%',
            marginTop: '15px',
            overflowY: 'auto',
          }}
        >
          {renderWeatherSection(filteredStations)}
        </div>
      )}
    </div>
  );
};

export default WeatherData;